import { useEffect, useRef } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { count, isNotNull } from "drizzle-orm";
import { MembershipPlan, coaches } from "~/../drizzle/schema";
import { getDBWithInit } from "~/lib/db";
import { gray, purple, violet } from "~/lib/ui";
import { createServerFn } from "@tanstack/react-start";

interface PlanCount {
  plan: MembershipPlan;
  count: number;
}

export const getCoachLevels = createServerFn({ method: "GET" }).handler(
  async () => {
    const counts: PlanCount[] = await (
      await getDBWithInit()
    )
      .select({
        plan: coaches.membershipPlan,
        count: count(),
      })
      .from(coaches)
      .where(isNotNull(coaches.ghlLocationId))
      .groupBy(coaches.membershipPlan);

    const total = counts.reduce((sum, { count }) => sum + Number(count), 0);

    return counts.map(({ plan, count }) => ({
      plan,
      count: Number(count),
      percentage: Math.round((Number(count) / total) * 100),
    }));
  },
);

function CoachLevelsPage() {
  const { data: coachLevels } = Route.useLoaderData();
  const svgRef = useRef<SVGSVGElement>(null);
  const labelRefs = useRef<(SVGTextElement | null)[]>([]);

  useEffect(() => {
    if (!svgRef.current || !labelRefs.current.length) {
      return;
    }

    // Update background rects based on text dimensions
    labelRefs.current.forEach((textEl) => {
      if (!textEl) {
        return;
      }
      const bbox = textEl.getBBox();
      const rect = textEl.previousElementSibling as SVGRectElement;

      const padding = 2;
      rect.setAttribute("x", String(bbox.x - padding));
      rect.setAttribute("y", String(bbox.y - padding / 2));
      rect.setAttribute("width", String(bbox.width + padding * 2));
      rect.setAttribute("height", String(bbox.height + padding));
    });
  }, [coachLevels]);

  const radius = 40;
  const strokeWidth = 12;
  const size = 100;
  const center = size / 2;
  const colors = {
    [MembershipPlan.Standard]: gray[100],
    [MembershipPlan.Pro]: purple[300],
    [MembershipPlan.ProPlus]: violet[500],
  };

  let currentAngle = 0;
  const segments = coachLevels.map((level) => {
    const percentage =
      level.count / coachLevels.reduce((sum, l) => sum + l.count, 0);
    const angle = percentage * 360;
    const largeArcFlag = 180 < angle ? 1 : 0;

    // Calculate start and end points on the circle
    const startX = center + radius * Math.cos((currentAngle * Math.PI) / 180);
    const startY = center + radius * Math.sin((currentAngle * Math.PI) / 180);
    const endX =
      center + radius * Math.cos(((currentAngle + angle) * Math.PI) / 180);
    const endY =
      center + radius * Math.sin(((currentAngle + angle) * Math.PI) / 180);

    // Calculate label position (middle of the arc)
    const labelAngle = currentAngle + angle / 2;
    const labelRadius = radius + 2; // Slightly outside the donut
    const labelX =
      center + labelRadius * Math.cos((labelAngle * Math.PI) / 180);
    const labelY =
      center + labelRadius * Math.sin((labelAngle * Math.PI) / 180);

    const segment = {
      d: `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`,
      color: colors[level.plan],
      label: {
        x: labelX,
        y: labelY,
        text: `${level.plan} ${level.percentage}%`,
        anchor: (labelX < center ? "start" : "end") as "start" | "end",
      },
    };

    currentAngle += angle;
    return segment;
  });

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-white p-8">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${size} ${size}`}
        className="max-w-screen max-h-screen"
        style={{
          width: "min(90vw, 90vh)",
          height: "min(90vw, 90vh)",
        }}
      >
        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth={strokeWidth}
        />

        {/* Segments */}
        {segments.map((segment, i) => (
          <path
            key={i}
            d={segment.d}
            fill="none"
            stroke={segment.color}
            strokeWidth={strokeWidth}
          />
        ))}

        {/* Labels */}
        {segments.map((segment, i) => (
          <g key={`label-${i}`}>
            <rect fill="white" opacity="0.6" rx="1" />
            <text
              ref={(el) => {
                labelRefs.current[i] = el;
              }}
              x={segment.label.x}
              y={segment.label.y}
              textAnchor={segment.label.anchor}
              className="font-heading text-[6px]"
              dominantBaseline="middle"
            >
              {segment.label.text}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

export const Route = createFileRoute("/embed/coach-levels/")({
  head: () => ({
    title: "Coach Levels Distribution - Dream Coach Match",
    meta: [
      {
        name: "description",
        content: "Embedded chart showing coach membership level distribution",
      },
    ],
  }),
  loader: async () => {
    const data = await getCoachLevels();
    return { data };
  },
  component: CoachLevelsPage,
});
